import Seo, { articleSchema, breadcrumbSchema, faqSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import HelpCta from "@/components/HelpCta";
import {
  Stethoscope,
  HeartPulse,
  Home,
  Users,
  BadgeCheck,
  ArrowRight,
  GraduationCap,
} from "lucide-react";

const STAGES = [
  {
    n: 1,
    title: "Desintoxicación",
    duration: "7–21 días",
    desc: "Supervisión médica 24/7, manejo del síndrome de abstinencia, estabilización psiquiátrica inicial.",
  },
  {
    n: 2,
    title: "Rehabilitación",
    duration: "3–12 meses",
    desc: "Terapias individuales y grupales, evaluación psiquiátrica, prevención de recaídas, trabajo familiar.",
  },
  {
    n: 3,
    title: "Reinserción social",
    duration: "Variable",
    desc: "Habilidades laborales, reconstrucción de vínculos, reinserción educacional, control toxicológico.",
  },
  {
    n: 4,
    title: "Seguimiento",
    duration: "12–24 meses",
    desc: "Controles periódicos, prevención de recaídas, participación en grupos de autoayuda.",
  },
];

const TYPES = [
  {
    icon: HeartPulse,
    title: "Ambulatorio Básico",
    bullet: "2–3 sesiones por semana",
    text: "Para consumo inicial o dependencia leve. La persona mantiene su vida cotidiana mientras recibe terapia.",
  },
  {
    icon: Stethoscope,
    title: "Ambulatorio Intensivo",
    bullet: "4–6 horas diarias, 5 días",
    text: "Para dependencia moderada. Combina terapia individual, grupal, médica y psiquiátrica con alta frecuencia.",
  },
  {
    icon: Home,
    title: "Residencial",
    bullet: "Internación 24/7",
    text: "Para dependencia severa. Separa a la persona del ambiente de consumo y ofrece contención total.",
  },
  {
    icon: Users,
    title: "Comunidad Terapéutica",
    bullet: "Programa 6–18 meses",
    text: "Programa largo basado en convivencia estructurada, trabajo, terapia y reinserción progresiva.",
  },
];

const THERAPIES = [
  { name: "Terapia Cognitivo-Conductual (TCC)", desc: "Identifica pensamientos y conductas asociadas al consumo y entrena respuestas alternativas." },
  { name: "Entrevista Motivacional", desc: "Enfoque respetuoso que ayuda a la persona a encontrar su propia motivación de cambio." },
  { name: "Prevención de Recaídas (Marlatt)", desc: "Modelo específico para mapear gatillos y construir estrategias de afrontamiento." },
  { name: "Terapia Familiar Sistémica", desc: "Trabaja con toda la familia como sistema, no solo con el consumidor." },
  { name: "Grupos de autoayuda (NA, AA)", desc: "Espacios gratuitos de pares en recuperación que se reúnen regularmente." },
];

const PUBLIC_RES = [
  { name: "SENDA", desc: "Programas financiados por el Estado en convenio con centros especializados." },
  { name: "COSAM", desc: "Centros Comunitarios de Salud Mental: atención ambulatoria en adicciones." },
  { name: "CESFAM", desc: "Atención primaria, puerta de entrada para derivación al tratamiento especializado." },
  { name: "FONASA + PRAIS", desc: "Coberturas de salud pública que incluyen atención por consumo de sustancias." },
];

const innerFaq = [
  {
    question: "¿Cuánto cuesta el tratamiento residencial privado?",
    answer:
      "Los programas residenciales privados en Chile oscilan entre $800.000 y $3.000.000 mensuales dependiendo de la infraestructura y duración. Existen opciones en el sistema público gratuitas con cupos limitados.",
  },
  {
    question: "¿Cuánto dura la abstinencia después del tratamiento?",
    answer:
      "La recuperación es un proceso que dura años. El tratamiento formal puede durar entre 6 y 18 meses, pero el seguimiento y la participación en grupos se recomienda mantener indefinidamente.",
  },
  {
    question: "¿Puedo tratarme gratis en Chile?",
    answer:
      "Sí. A través de CESFAM, COSAM, programas SENDA y coberturas FONASA/PRAIS el tratamiento para consumo problemático es gratuito o de muy bajo costo en Chile.",
  },
];

export default function Tratamientos() {
  const crumbs = [{ name: "Tratamientos", path: "/tratamientos" }];
  const schemas = [
    articleSchema({
      title: "Tratamiento para la Pasta Base: Tipos, Duración y Dónde Encontrar Ayuda",
      description:
        "Guía completa de tratamientos para la adicción a pasta base en Chile: desintoxicación, terapia ambulatoria, internación, duración estimada y cómo acceder a centros especializados.",
      path: "/tratamientos",
    }),
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
    faqSchema(innerFaq),
  ];

  return (
    <>
      <Seo
        title="Tratamiento para la Pasta Base: Tipos, Duración y Dónde Encontrar Ayuda | pasta-base.cl"
        description="Guía completa de tratamientos para la adicción a pasta base en Chile: desintoxicación, terapia ambulatoria, internación, duración estimada y cómo acceder a centros especializados."
        path="/tratamientos"
        type="article"
        schema={schemas}
      />

      <article className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />
        <header className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            Guía clínica
          </span>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Tratamiento para la Adicción a la Pasta Base
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-300">
            Etapas, modalidades, terapias específicas y opciones gratuitas en
            Chile. La información que necesitas para tomar una decisión
            informada sobre el mejor camino para ti o tu familia.
          </p>
        </header>

        {/* Sí se puede */}
        <section className="mt-12 rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-slate-900 p-8">
          <div className="flex items-start gap-4">
            <BadgeCheck className="h-8 w-8 shrink-0 text-emerald-300" />
            <div>
              <h2 className="text-2xl font-bold text-slate-50 sm:text-3xl">
                ¿Se puede dejar la pasta base?
              </h2>
              <p className="mt-3 max-w-2xl text-base text-slate-200">
                <strong className="font-semibold text-emerald-300">Sí, con tratamiento profesional adecuado.</strong>{" "}
                Miles de personas en Chile se recuperan cada año. Requiere
                tiempo, acompañamiento clínico y una red familiar involucrada,
                pero es completamente posible.
              </p>
            </div>
          </div>
        </section>

        {/* Etapas */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold tracking-tight text-slate-50">
            Etapas del tratamiento
          </h2>
          <p className="mt-3 max-w-3xl text-slate-300">
            La recuperación no es un evento, es un proceso con etapas bien
            definidas. Cada etapa cumple una función distinta y saltarse una de
            ellas suele producir recaídas.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {STAGES.map((s) => (
              <div
                key={s.n}
                className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6"
              >
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/10 text-sm font-bold text-emerald-300 ring-1 ring-emerald-500/30">
                    {s.n}
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-50">{s.title}</h3>
                    <p className="text-xs text-slate-400">{s.duration}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tipos */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold tracking-tight text-slate-50">Tipos de tratamiento</h2>
          <p className="mt-3 max-w-3xl text-slate-300">
            No todos los casos requieren la misma modalidad. Un equipo clínico
            evalúa el perfil, la severidad y el entorno para recomendar el tipo
            adecuado.
          </p>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            {TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.title}
                  className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
                      <Icon className="h-5 w-5 text-emerald-300" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-50">{t.title}</h3>
                      <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-emerald-400">
                        {t.bullet}
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-slate-300">{t.text}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Terapias */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold tracking-tight text-slate-50">
            Terapias específicas para pasta base
          </h2>
          <div className="mt-8 divide-y divide-slate-800 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            {THERAPIES.map((t) => (
              <div key={t.name} className="flex flex-col gap-2 p-6 sm:flex-row sm:items-center sm:gap-8">
                <div className="sm:w-64 sm:shrink-0">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4 text-cyan-300" />
                    <h3 className="text-base font-semibold text-slate-50">{t.name}</h3>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-slate-400">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Gratis en Chile */}
        <section className="mt-16">
          <div className="rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-slate-900 p-8">
            <h2 className="text-3xl font-bold tracking-tight text-slate-50">
              Tratamiento gratuito en Chile
            </h2>
            <p className="mt-3 max-w-3xl text-slate-300">
              El Estado chileno ofrece tratamiento gratuito a través de una red
              pública. Puede tener lista de espera, pero funciona y salva vidas.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {PUBLIC_RES.map((r) => (
                <div key={r.name} className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
                  <div className="text-sm font-bold text-cyan-300">{r.name}</div>
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">{r.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm text-slate-300">
              <strong className="font-semibold text-slate-100">Primer paso:</strong>{" "}
              llama al <a href="tel:1412" className="text-cyan-300 hover:underline">Fono Drogas 1412</a>{" "}
              o acude al CESFAM más cercano.
            </p>
          </div>
        </section>

        {/* Costos privados */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold tracking-tight text-slate-50">
            ¿Cuánto cuesta el tratamiento privado?
          </h2>
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-5 py-3">Modalidad</th>
                  <th className="px-5 py-3">Rango mensual (CLP)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-900/40 text-slate-200">
                <tr>
                  <td className="px-5 py-4 font-medium text-slate-100">Ambulatorio básico</td>
                  <td className="px-5 py-4 text-slate-300">$200.000 – $400.000</td>
                </tr>
                <tr>
                  <td className="px-5 py-4 font-medium text-slate-100">Ambulatorio intensivo</td>
                  <td className="px-5 py-4 text-slate-300">$400.000 – $700.000</td>
                </tr>
                <tr>
                  <td className="px-5 py-4 font-medium text-slate-100">Residencial</td>
                  <td className="px-5 py-4 text-slate-300">$800.000 – $3.000.000</td>
                </tr>
                <tr>
                  <td className="px-5 py-4 font-medium text-slate-100">Comunidad terapéutica</td>
                  <td className="px-5 py-4 text-slate-300">$600.000 – $1.500.000</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Los rangos son referenciales, a febrero de 2026. Varían según
            ubicación, infraestructura y equipo clínico.
          </p>
        </section>

        {/* CTA */}
        <div className="mt-14 flex flex-wrap items-center gap-4 rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-950 p-8">
          <p className="flex-1 text-base text-slate-200 sm:text-lg">
            <strong className="font-semibold text-emerald-300">Encuentra el centro adecuado para ti.</strong>{" "}
            Red SinAdicciones.org reúne centros verificados en las 16 regiones
            de Chile.
          </p>
          <a
            href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tratamientos-cta-btn"
            className="btn-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
          >
            Buscar Centros
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </article>

      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <HelpCta testidPrefix="tratamientos" />
        </div>
      </section>
    </>
  );
}
